// MAINTAINER-ONLY FILE — do not edit for prototype work.
// New pages → owner shards (src/routes/owner-shards/).
// New app-wide providers → require explicit maintainer approval.
// See docs/platform/conventions/playground-architecture.md.
// eslint-disable-next-line no-restricted-imports
import { IntlProvider } from "@faire/localization/react/IntlProvider";
import { LoginCallback } from "@okta/okta-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Suspense, useEffect, useLayoutEffect } from "react";
import {
  BrowserRouter,
  Switch,
  Redirect,
  Route,
  generatePath,
  useLocation,
} from "react-router-dom";

import { useFlag } from "./api/flags";
import "./App.css";
import { SecureRoute } from "./components/auth/SecureRoute";
import { RouteLoadingFallback } from "./components/RouteLoadingFallback";
import { LazyFloatingTerminal } from "./components/terminal/LazyFloatingTerminal";
import { AuthProvider } from "./contexts/AuthContext";
import { PlaygroundLocalizationProvider } from "./platform/localization";
import { ComponentsRoutes } from "./routes/components";
import { ownerShards } from "./routes/owner-shards";
import { PatternsRoutes } from "./routes/patterns";
import {
  BrandActivePromotionDetailPage,
  BrandFundedPromoModalPage,
  BrandPromotionsPerformancePage,
  CommunityLibraryPage,
  FauxDSPage,
  FeedPage,
  GettingStartedPage,
  ImageCatalogPage,
  LoginPage,
  NotFoundPage,
  PlaybookPage,
  PlaygroundPages,
} from "./routes/playground";
import { prototypeRouteRedirects } from "./routes/prototypeRouteRedirects";
import { WorkspacesRoutes } from "./routes/workspaces";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    // Disable browser's automatic scroll restoration
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    // Scroll to top synchronously before paint to prevent visible jump
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);

  return null;
}

function AppContent() {
  // Verification probe for the Vercel Flags integration. Logs the resolved
  // value of `launch-page-v1` once /api/flags responds. Remove or repurpose
  // when the first prototype starts gating behavior on a flag.
  const launchPageV1 = useFlag("launch-page-v1");
  useEffect(() => {
    if (launchPageV1 !== undefined) {
      // eslint-disable-next-line no-console
      console.log("[flags] launch-page-v1:", launchPageV1);
    }
  }, [launchPageV1]);

  return (
    <div className="App">
      <div className="compass-content-wrapper">
        <Suspense fallback={<RouteLoadingFallback />}>
          <Switch>
            <SecureRoute
              unprotected
              path="/login/callback"
              component={LoginCallback}
            />
            <SecureRoute unprotected path="/login" component={LoginPage} />
            <SecureRoute path="/platform" component={PlaygroundPages} />
            <SecureRoute exact path="/" component={FeedPage} />
            <SecureRoute
              path="/getting-started"
              component={GettingStartedPage}
            />
            <SecureRoute path="/playbook" component={PlaybookPage} />
            <SecureRoute path="/experimental" component={FeedPage} />
            <SecureRoute
              path={["/patterns", "/templates"]}
              component={PatternsRoutes}
            />
            {ownerShards.map(({ slug, component }) => (
              <SecureRoute key={slug} path={`/${slug}`} component={component} />
            ))}
            <SecureRoute path="/workspaces" component={WorkspacesRoutes} />
            <SecureRoute path="/slate" component={FauxDSPage} />
            <Redirect exact from="/fauxds" to="/slate" />
            <SecureRoute path="/image-catalog" component={ImageCatalogPage} />
            {prototypeRouteRedirects.map(({ from, to }) => (
              <Route
                key={from}
                path={from}
                render={({ location, match }) => (
                  <Redirect
                    to={`${generatePath(to, match.params)}${location.pathname.slice(match.url.length)}${location.search}`}
                  />
                )}
              />
            ))}
            <Redirect exact from="/design-system" to="/slate" />{" "}
            {/* Redirect for old bookmarks */}
            <Redirect exact from="/ideas" to="/rl/themes/ideas" />
            <SecureRoute path="/components" component={ComponentsRoutes} />
            <SecureRoute
              path="/community-library"
              component={CommunityLibraryPage}
            />
            {/* Brand Portal Template Routes */}
            <SecureRoute
              path="/faire/fulfilled-by-faire"
              render={({ location }) => (
                <Redirect
                  to={{
                    pathname: "/kv/faire/catalog-dashboard-fbf",
                    search: location.search,
                  }}
                />
              )}
            />
            <SecureRoute
              exact
              path="/faire/promoted-listings-2027-v2"
              render={() => <Redirect to="/tm/faire/promoted-listings-2027" />}
            />
            <SecureRoute
              exact
              path="/brand-promotions-performance/p/:promotionId"
              component={BrandActivePromotionDetailPage}
            />
            <SecureRoute
              exact
              path="/brand-promotions-performance"
              component={BrandPromotionsPerformancePage}
            />
            <SecureRoute
              exact
              path="/brand-funded-promo-modal"
              component={BrandFundedPromoModalPage}
            />
            <SecureRoute component={NotFoundPage} />
          </Switch>
        </Suspense>
      </div>
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <IntlProvider>
            <PlaygroundLocalizationProvider>
              <ScrollToTop />
              <AppContent />
            </PlaygroundLocalizationProvider>
          </IntlProvider>
          <LazyFloatingTerminal />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
